import {
  PAGE_SIZES,
  pageItems,
  pageOffset,
  type PageInfo,
  type PageSize,
} from '@myrewards/contracts';
import { useId, useState, type SyntheticEvent, type ReactElement, type ReactNode } from 'react';
import {
  Button as AriaButton,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
} from 'react-aria-components';
import { formatCount } from './format';
import { Icon } from './Icon';
import f from './fields.module.css';
import s from './Pager.module.css';

export interface PagerProps {
  /** The table's name, for the navigation landmark ("‹label› pages"). */
  label: string;
  page: PageInfo;
  onPage: (n: number) => void;
  onSize: (size: PageSize) => void;
  /** The next page is on its way: say so and hold every control. */
  pending?: boolean;
  /** The page on its way, for the loading count; the page still showing when left out. */
  loadingPage?: number | undefined;
}

/** A whole number from 1 to `pages`, or null. */
function parsePage(text: string, pages: number): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= 1 && n <= pages ? n : null;
}

function Arrow({
  direction,
  disabled,
  onClick,
  busy = false,
}: {
  direction: 'previous' | 'next';
  disabled: boolean;
  onClick: () => void;
  busy?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      className={s.arrow}
      aria-label={direction === 'previous' ? 'Previous page' : 'Next page'}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={busy ? 'loader' : direction === 'previous' ? 'chevronLeft' : 'chevronRight'} />
    </button>
  );
}

function PageError({ id, pages }: { id: string; pages: number }): ReactElement {
  return (
    <p id={id} className={s.error} role="alert">
      <Icon name="alert" size={16} />
      Pick a page from 1 to {formatCount(pages)}.
    </p>
  );
}

/** The narrow layout's control: arrows around an editable page number. */
function PageBox({
  current,
  pages,
  disabled,
  onPage,
}: {
  current: number;
  pages: number;
  disabled: boolean;
  onPage: (n: number) => void;
}): ReactElement {
  const id = useId();
  const [text, setText] = useState(String(current));
  const [bad, setBad] = useState(false);
  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    const n = parsePage(text, pages);
    if (n === null) {
      setBad(true);
      return;
    }
    setBad(false);
    if (n === current) setText(String(current));
    else onPage(n);
  };
  return (
    <form className={s.box} onSubmit={submit}>
      <div className={s.boxRow}>
        <Arrow
          direction="previous"
          disabled={disabled || current === 1}
          onClick={() => {
            onPage(current - 1);
          }}
        />
        <label htmlFor={id}>Page</label>
        <input
          id={id}
          className={bad ? `${s.input} ${s.bad}` : s.input}
          type="text"
          inputMode="numeric"
          value={text}
          disabled={disabled}
          aria-invalid={bad ? true : undefined}
          aria-describedby={bad ? `${id}-error` : undefined}
          onChange={(event) => {
            setText(event.target.value);
            setBad(false);
          }}
        />
        <span>of {formatCount(pages)}</span>
        <Arrow
          direction="next"
          disabled={disabled || current === pages}
          onClick={() => {
            onPage(current + 1);
          }}
        />
      </div>
      {bad && <PageError id={`${id}-error`} pages={pages} />}
    </form>
  );
}

function JumpForm({
  current,
  pages,
  disabled,
  onPage,
}: {
  current: number;
  pages: number;
  disabled: boolean;
  onPage: (n: number) => void;
}): ReactElement {
  const id = useId();
  const [text, setText] = useState('');
  const [bad, setBad] = useState(false);
  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    const n = parsePage(text, pages);
    if (n === null) {
      setBad(true);
      return;
    }
    setBad(false);
    if (n === current) setText('');
    else onPage(n);
  };
  return (
    <form className={s.jump} onSubmit={submit}>
      <div>
        <div className={s.jumpRow}>
          <label htmlFor={id}>Go to page</label>
          <input
            id={id}
            className={bad ? `${s.input} ${s.bad}` : s.input}
            type="text"
            inputMode="numeric"
            value={text}
            disabled={disabled}
            aria-invalid={bad ? true : undefined}
            aria-describedby={bad ? `${id}-error` : undefined}
            onChange={(event) => {
              setText(event.target.value);
              setBad(false);
            }}
          />
          <button type="submit" className={s.go} disabled={disabled}>
            Go
          </button>
        </div>
        {bad && <PageError id={`${id}-error`} pages={pages} />}
      </div>
    </form>
  );
}

function RowsPerPage({
  size,
  disabled,
  onSize,
}: {
  size: number;
  disabled: boolean;
  onSize: (size: PageSize) => void;
}): ReactElement {
  return (
    <AriaSelect
      className={s.size}
      aria-busy={disabled ? true : undefined}
      isDisabled={disabled}
      value={String(size)}
      onChange={(key) => {
        const chosen = PAGE_SIZES.find((n) => String(n) === String(key));
        if (chosen !== undefined && chosen !== size) onSize(chosen);
      }}
    >
      <Label>Rows per page</Label>
      <AriaButton className={s.select}>
        <SelectValue />
        <Icon name="chevronDown" size={16} />
      </AriaButton>
      <Popover className={f.popover}>
        <ListBox className={f.listbox} items={PAGE_SIZES.map((n) => ({ id: String(n) }))}>
          {(item) => (
            <ListBoxItem id={item.id} textValue={item.id} className={f.option}>
              {item.id}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

/**
 * The pager under every long table (APP-135): where this page sits, numbered pages, Rows per page,
 * and Go to page past seven pages. The wrapper decides the layout from its own width, so the same
 * pager reads in a full-width card, a Settings-width card and a phone column.
 */
export function Pager({
  label,
  page,
  onPage,
  onSize,
  pending = false,
  loadingPage,
}: PagerProps): ReactElement | null {
  if (page.total === 0) return null;
  const { number: current, pages, size, total } = page;
  const first = pageOffset(page) + 1;
  const last = Math.min(total, current * size);
  // Rows per page is worth showing whenever a smaller size would split the list.
  const showSize = total > PAGE_SIZES[0];

  const count: ReactNode = pending ? (
    `Loading page ${formatCount(loadingPage ?? current)}…`
  ) : pages === 1 ? (
    <>
      Showing all <strong>{formatCount(total)}</strong>
    </>
  ) : (
    <>
      Showing{' '}
      <strong>
        {formatCount(first)}–{formatCount(last)}
      </strong>{' '}
      of <strong>{formatCount(total)}</strong>
    </>
  );

  return (
    <div className={s.wrap}>
      <nav className={s.pager} aria-label={`${label} pages`} aria-busy={pending ? true : undefined}>
        <div className={s.info}>
          <p className={s.count} aria-live="polite">
            {count}
          </p>
          {showSize && <RowsPerPage size={size} disabled={pending} onSize={onSize} />}
        </div>
        {pages > 1 && (
          <>
            <ul className={s.pages}>
              <li>
                <Arrow
                  direction="previous"
                  disabled={pending || current === 1}
                  onClick={() => {
                    onPage(current - 1);
                  }}
                />
              </li>
              {pageItems(current, pages).map((item, index) =>
                item === null ? (
                  <li key={`gap-${index}`} className={s.gap} aria-hidden="true">
                    …
                  </li>
                ) : (
                  <li key={item}>
                    <button
                      type="button"
                      className={s.number}
                      aria-label={`Page ${item}${item === pages && pages > 7 ? ', the last' : ''}`}
                      aria-current={item === current ? 'page' : undefined}
                      disabled={pending}
                      onClick={() => {
                        onPage(item);
                      }}
                    >
                      {item}
                    </button>
                  </li>
                ),
              )}
              <li>
                <Arrow
                  direction="next"
                  disabled={pending || current === pages}
                  busy={pending}
                  onClick={() => {
                    onPage(current + 1);
                  }}
                />
              </li>
            </ul>
            <PageBox
              key={current}
              current={current}
              pages={pages}
              disabled={pending}
              onPage={onPage}
            />
            {pages > 7 && (
              <JumpForm current={current} pages={pages} disabled={pending} onPage={onPage} />
            )}
          </>
        )}
      </nav>
    </div>
  );
}
